import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import 'material-icons/iconfont/outlined.css'
import { bindActionCreators } from 'redux'
import moment from 'moment'

import DateInput from '@components/inputs/textInput/dateInput'
import CheckBox from '@components/inputs/checkBox'
import SecondaryButton from '@components/inputs/button/secondaryButton'
import PrimaryButton from '@components/inputs/button/primaryButton'
import ConfirmationModal from '@components/modal/confirmationModal'

import {
  isNormalFormatValidDate,
  getDateStringFromFormattedHDateStr
} from '@util/timeUtil'

import {
  CloseIcon,
  Area,
  CheckBoxArea,
  RowGap,
  Container,
  Section,
} from './styles'
import { AreaLabel, Title } from '../styles'

import { listEquipmentsByInsurer as listEquipmentsByInsurerAction } from '@stores/actions/equipment'

const LoanFilterModal = ({
  setFilterState,
  isOpen,
  setClose,
  listEquipmentsByInsurer,
  equipments,
  isOpenLoans
}) => {

  const [selectedInterval, setSelectedInterval] = useState(null)
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const [selectedEquipments, setSelectedEquipments] = useState([])

  useEffect(() => {
    listEquipmentsByInsurer()
  }, [])

  const onClean = () => {
    setSelectedInterval(null)
    setStartDate(null)
    setEndDate(null)
    setSelectedEquipments([])

    setFilterState(
      {
        startDate: null,
        endDate: null,
        timeInterval: null,
        equipments: null,
      }
    )
  }

  const onApply = () => {
    let finalStartDate = null
    let finalEndDate = null

    if (selectedInterval) {

      finalEndDate = moment().endOf('day').format('YYYY-MM-DD HH:mm:ss')

      let pastDate = moment()
      if (selectedInterval === 'lastWeek') pastDate = pastDate.subtract(7, 'days')
      if (selectedInterval === 'lastMonth') pastDate = pastDate.subtract(1, 'months')
      if (selectedInterval === 'last15Days') pastDate = pastDate.subtract(15, 'days')
      if (selectedInterval === 'last6Months') pastDate = pastDate.subtract(6, 'months')

      finalStartDate = pastDate.startOf('day').format('YYYY-MM-DD HH:mm:ss')
    } else {
      finalStartDate = startDate === null || startDate === ''
        ? null
        : getDateStringFromFormattedHDateStr(startDate)
      finalEndDate = endDate === null || endDate === ''
        ? null
        : moment(getDateStringFromFormattedHDateStr(endDate))
          .endOf('day')
          .format('YYYY-MM-DD HH:mm:ss')
    }

    const finalEquipments = selectedEquipments.length > 0 ? selectedEquipments : null

    const filterState = {
      startDate: finalStartDate,
      endDate: finalEndDate,
      timeInterval: null,
      equipments: finalEquipments,
    }

    setFilterState(filterState)
    setClose()
  }

  const handleIntervalChange = (interval) => {
    if (interval === selectedInterval) {
      setSelectedInterval(null)
    } else {
      setSelectedInterval(interval)
    }
  }

  const handleStartDateChange = (e) => {
    setStartDate(e)
  }

  const handleEndDateChange = (e) => {
    setEndDate(e)
  }

  const invalidDates = () => {
    if (!startDate && !endDate) return false

    const startValid = startDate ? isNormalFormatValidDate(startDate) : true
    const endValid = endDate ? isNormalFormatValidDate(endDate) : true

    if (!startValid || !endValid) {
      return true
    }

    return false
  }

  const handleEquipmentChange = (equipment) => {
    const updatedSelectedEquipments = [...selectedEquipments]
    const index = updatedSelectedEquipments.indexOf(equipment)

    if (index === -1) {
      updatedSelectedEquipments.push(equipment)
    } else {
      updatedSelectedEquipments.splice(index, 1)
    }
    setSelectedEquipments(updatedSelectedEquipments)
  }

  return (
    <ConfirmationModal isOpen={isOpen}>
      <Container>
        <CloseIcon className="material-icons-outlined" onClick={setClose}>close</CloseIcon>
        <Title>Filtrar pesquisa</Title>
        <Section>
          <RowGap>
            <Area>
              <AreaLabel>Data inicial</AreaLabel>
              <DateInput
                value={startDate}
                onChange={handleStartDateChange}
              />
            </Area>
            <Area>
              <AreaLabel>Data final</AreaLabel>
              <DateInput
                value={endDate}
                onChange={handleEndDateChange}
              />
            </Area>
          </RowGap>
        </Section>
        {isOpenLoans && (
          <Section>
            <AreaLabel>Empréstimos próximos do vencimento</AreaLabel>
            <CheckBoxArea>
              <CheckBox
                checked={selectedInterval === 'lastWeek'}
                onChange={() => handleIntervalChange('lastWeek')}
                label="Última semana"
              />
              <CheckBox
                checked={selectedInterval === 'lastMonth'}
                onChange={() => handleIntervalChange('lastMonth')}
                label="Último mês"
              />
              <CheckBox
                checked={selectedInterval === 'last15Days'}
                onChange={() => handleIntervalChange('last15Days')}
                label="Últimos 15 dias"
              />
              <CheckBox
                checked={selectedInterval === 'last6Months'}
                onChange={() => handleIntervalChange('last6Months')}
                label="Últimos 6 meses"
              />
            </CheckBoxArea>
          </Section>
        )}
        {equipments && equipments.length > 0 && (
          <Section>
            <AreaLabel>Equipamentos</AreaLabel>
            <CheckBoxArea>
              {equipments.map((equipment) => (
                <CheckBox
                  key={equipment.value}
                  checked={selectedEquipments.includes(equipment.value)}
                  onChange={() => handleEquipmentChange(equipment.value)}
                  label={equipment.label}
                />
              ))}
            </CheckBoxArea>
          </Section>
        )}
        <RowGap>
          <SecondaryButton onClick={onClean}>Limpar</SecondaryButton>
          <PrimaryButton onClick={onApply} disabled={invalidDates()}>
            Aplicar
          </PrimaryButton>
        </RowGap>
      </Container>
    </ConfirmationModal>
  )
}

LoanFilterModal.propTypes = {
  isOpen: PropTypes.bool,
  setClose: PropTypes.func.isRequired,
  listEquipmentsByInsurer: PropTypes.func.isRequired,
  equipments: PropTypes.arrayOf(PropTypes.object),
  setFilterState: PropTypes.func.isRequired,
  isOpenLoans: PropTypes.bool,
}

LoanFilterModal.defaultProps = {
  isOpen: false,
  equipments: null,
  isOpenLoans: false,
}

const mapStateToProps = ({ equipment }) => ({
  equipments: equipment.list,
})

const mapDispatchToProps = (dispatch) => bindActionCreators(
  {
    listEquipmentsByInsurer: listEquipmentsByInsurerAction,
  },
  dispatch
)

export default connect(mapStateToProps, mapDispatchToProps)(LoanFilterModal)
